<template>
    <div class="md-layout" v-if="application!=null">
        <md-dialog-alert
            :md-active.sync="error_dialog.show"
            :md-title="error_dialog.title"
            :md-content="error_dialog.content"/>
        <div class="md-layout-item md-size-50">
            <group-card title="Lender Details" icon="account_balance">
                <div class="md-layout">
                    <!-- Lender -->
                    <validated-text-input class="md-layout-item md-small-size-100 md-size-50"
                                          label="Lender"
                                          v-model="currentLender.lender_name" disabled>
                    </validated-text-input>

                    <!-- Tier -->
                    <validated-autocomplete-input class="md-layout-item md-small-size-100 md-size-50"
                                            label="Tier"
                                            v-model="currentLender.product_name"
                                            :options="available_products" disabled
                                            force
                    />

                </div>
            </group-card>
        </div>
        <div class="md-layout-item md-size-50">
            <group-card title="Lender Fees" icon="paid">
                <div class="md-layout">
                    <validated-input type="CURRENCY"
                                            class="md-layout-item md-small-size-100 md-size-50"
                                            label="DOF"
                                            prefix="$"
                                            v-model="submission.document_origination_fee"
                                            :disabled="isSubmitted"
                                            />
                    <validated-input type="CURRENCY"
                                            class="md-layout-item md-small-size-100 md-size-50"
                                            label="Establishment Fees"
                                            prefix="$"
                                            v-model="submission.establishment_fee"
                                            :disabled="isSubmitted"
                                            />
                    <validated-input type="CURRENCY"
                                            class="md-layout-item md-small-size-100 md-size-50"
                                            label="PPSR"
                                            prefix="$"
                                            v-model="submission.ppsr_fee"
                                            :disabled="isSubmitted"
                                            />
                    <validated-input type="CURRENCY"
                                            class="md-layout-item md-small-size-100 md-size-50"
                                            label="Risk"
                                            prefix="$"
                                            v-model="submission.risk_fee"
                                            :disabled="isSubmitted"
                                            />
                </div>
            </group-card>
        </div>
        <!-- Insurance Start -->
        <div class="md-layout-item md-size-100">
            <InsuranceComponent :insurances="application.loan.insurances"
                                v-if="application!=null" :disabled="isSubmitted">
            </InsuranceComponent>
        </div>
        <!-- Insurance End -->

        <!-- Income Start -->
        <div class="md-layout-item md-size-50">
            <group-card title="Deal Income" icon="local_offer" padded>
                <div class="list-item">
                    <div class="md-layout">
                        <validated-input type="CURRENCY"
                                        class="md-layout-item md-small-size-100 md-size-30"
                                        label="DOF"
                                        prefix="$"
                                        v-model="submission.document_origination_fee"
                                        v-on:input="calculateIncome"
                                        :disabled="isSubmitted"
                                        />
                        <validated-input type="CURRENCY"
                                        class="md-layout-item md-small-size-100 md-size-30"
                                        label="Lender Commission"
                                        prefix="$"
                                        v-model="new_income.lender_commission.amount"
                                        :disabled="isSubmitted"
                                        />
                      <validated-input type="CURRENCY"
                                       class="md-layout-item md-small-size-100 md-size-30"
                                       label="NAF (calculated)"
                                       prefix="$"
                                       v-model="naf"
                                       disabled
                      />
                      <md-checkbox type="checkbox" class="md-layout-item md-small-size-30 md-size-30"
                                   v-model="new_income.lender_commission.include_in_naf"
                      >Include Commission in NAF</md-checkbox>
                    </div>
                    <div class="md-layout">
                      <div v-for="insurance in application.loan.insurances" :key="insurance.type">
                        <div class="md-layout">
                          <validated-input type="CURRENCY"
                                           class="md-layout-item md-small-size-100 md-size-100"
                                           :label="'Income '+insurance.type" prefix="$"
                                           :value="insuranceIncome(insurance)"
                                           @input="calculateInsuranceIncome(insurance, $event)"
                                           :disabled="isSubmitted || !insurance.enabled"
                          />
                        </div>
                      </div>
                    </div>
                </div>
                <span class="md-title">Total: {{ new_income.totalIncome | currency }}</span>
              <md-card-actions>
                <!--SDB-565 temporary remeove compliance notification-->
                <!-- <p :class="{ 'disabled_message': !compliance, 'success_message': compliance }" class="margin-right-15">Compliance: {{compliance ? 'Pass': 'Fail'}}</p> -->
                <p v-if="isFinalized" class="disabled_message margin-right-15">This deal has already been finalized.</p>
                <p v-if="isSaveDisabledWithInsurances()" class="disabled_message margin-right-15">All insurance details must be entered before you can save income.</p>
                <p v-if="isSaveDisabledWithExpenses()" class="disabled_message margin-right-15">Referral Fee must be entered when a Referrer is selected.</p>

                <loading-icon-text-button
                  @click="save('saveIncome')"
                  icon="save"
                  v-if="!isSubmitted"
                  :disabled="is_save_disabled() || isFinalized"
                >
                  Save Income
                </loading-icon-text-button>
              </md-card-actions>
            </group-card>
        </div>
        <!-- Income End -->

        <!-- Expenses Start -->
        <div class="md-layout-item md-size-50">
            <group-card title="Deal Expenses" icon="attach_money" padded>
                <div class="list-item" v-for="(expense, index) in expenses" :key=index>
                    <div class="md-layout my-2">
                      <span class="md-layout-item md-small-size-10 md-size-5 expense-warning">
                        <note-view :status="expense.isSaved ? 'good' : 'neutral'"></note-view>
                      </span>
                      <validated-autocomplete-input
                        class="md-layout-item md-small-size-100 md-size-30"
                        label="Expense Type"
                        v-model="expense.type"
                        :options="settlement_expense_types_values"
                        :disabled="isSubmitted"
                        v-on:input="changeExpenseStatus(index)"
                        force
                      >
                      </validated-autocomplete-input>
                      <validated-input type="CURRENCY"
                                       class="md-layout-item md-small-size-100 md-size-30"
                                       label="Amount"
                                       v-model="expense.value"
                                       prefix="$"
                                       :disabled="isSubmitted"
                                       v-on:input="changeExpenseStatus(index, true)"
                      />
                      <!-- Remove Button -->
                      <div class="md-layout-item md-size-30 text-align-right">
                            <md-button class="md-primary md-simple md-danger close-button" @click="removeItem(expense)"
                                       :disabled="isSubmitted">
                                <md-icon>close</md-icon>
                            </md-button>
                      </div>
                      <div v-if="show_payee_details(expense.type)">
                        <div class="md-layout">
                          <!-- First Name -->
                          <validated-text-input
                            class="md-layout-item md-small-size-100 md-size-30"
                            label="Payee First Name"
                            v-model="expense.payee_first_name"
                            :disabled="isSubmitted"
                            regex="^[A-Za-z '-]*$"
                            required
                          />
                          <!-- Last Name -->
                          <validated-text-input
                            class="md-layout-item md-small-size-100 md-size-30"
                            label="Payee Last Name"
                            :disabled="isSubmitted"
                            v-model="expense.payee_last_name"
                            regex="^[A-Za-z '-]*$"
                            required
                          />
                          <!-- Email -->
                          <validated-text-input
                            class="md-layout-item md-small-size-100 md-size-30"
                            label="Payee Email"
                            v-model="expense.payee_email"
                            melissaCheckType="email"
                            :disabled="isSubmitted"
                            required
                          />
                        </div>
                      </div>
                      <div class="md-layout" v-if="show_shared_broker(expense.type)">
                        <!-- Broker list -->
                        <validated-autocomplete-input
                          class="md-layout-item md-small-size-100 md-size-50"
                          label="Broker"
                          v-model="expense.broker_shared_fee"
                          :onItemSelected="setBrokerPod(index)"
                          :options="list_of_brokers"
                          :disabled="isSubmitted"
                          :displaylabel="'name'"
                          required
                          force
                        />
                        <validated-autocomplete-input
                          class="md-layout-item md-small-size-100 md-size-50"
                          label="Pod"
                          v-model="brokerPod[index]"
                          :disabled="true"
                        />
                      </div>
                    </div>
                    <md-divider></md-divider>
                </div>
                <md-button class="md-primary md-sm" @click="addExpense()" v-if="!isSubmitted">Add</md-button>
                <br>
                <span class="md-title">Total: {{ totalExpenses | currency }}</span>
                <div v-if="expense_warning.show" class="md-layout-item md-small-size-100 md-size-100">
                  <note-view status="neutral">{{ expense_warning.message }}</note-view>
                </div>
            </group-card>
        </div>
        <div class="md-layout-item md-size-100">
            <group-card title="Finalize" icon="segment" padded :loading="loading_tax_invoice_request_document">
              <template slot="actions">
                <!-- Generate Document Dropdowns -->
                <md-menu md-size="medium" md-align-trigger class="generate_dropdown" v-if="!isSubmitted">
                  <md-button md-menu-trigger class="md-primary"><md-icon>description</md-icon> Generate Documents <md-icon>arrow_drop_down</md-icon></md-button>
                  <md-menu-content>
                    <md-menu-item @click="generateInvoice">Tax Invoice Request</md-menu-item>
                    <md-menu-item @click="generateBrokCommInvoice">Brokerage & Commission Invoice</md-menu-item>
                  </md-menu-content>
                </md-menu>
                <!--  goto documents page -->
                <loading-icon-text-button
                  class="md-primary md-sm"
                  @click="$router.push(`/application/${application.loan.id}/documents`)"
                  icon="attach_file"
                >
                  Goto Documents Page
                </loading-icon-text-button>
              </template>
                <div class="list-item">
                    <div class="md-layout">
                        <!-- Settlement Date -->
                        <validated-input
                            class="md-layout-item md-small-size-100 md-size-30"
                            label="Settlement Date"
                            v-model="settlement_date"
                            :disabled="isSubmitted"
                            type="DATE"
                        />
                        <div class="lender_app_id_input">
                          <ValidationObserver ref="lender_application_id_observer">
                            <validated-text-input
                                class="md-layout-item md-small-size-100 md-size-30"
                                label="Lender Application Id"
                                v-model="lender_application_id"
                                :minLength=1
                                errormsg="Please enter a lender application Id"
                                ref="observer"
                                required
                            />
                          </ValidationObserver>
                        </div>
                    </div>
                    <p v-if="isSettlementDateToFar" class="disabled_message mr-lt-15">Settlement date cannot be prior to current month </p>
                </div>
              <md-card-actions>
                <NotificationCard v-if="isFinalized" message="This deal has already been finalized."></NotificationCard>
                <NotificationCard v-else message="This deal is yet to be finalized." icon="warning_amber" type="alert"></NotificationCard>
                <loading-icon-text-button
                  @click="save('saveUpdate')"
                  style="margin-right: 5px;"
                  icon="history"
                  v-if='userHasRole("Compliance Officer") && !isSubmitted'
                  :disabled="is_save_disabled(true)"
                >
                  Save Update
                </loading-icon-text-button>
                <!--SDB-565 remove compliance check (:disabled="is_save_disabled(true) || !compliance")-->
                <loading-icon-text-button
                  @click="save('saveIncomeAndFinalize')"
                  icon="save"
                  v-if="!isSubmitted"
                  :disabled="is_save_disabled(true)"
                >
                  Finalize Income
                </loading-icon-text-button>
              </md-card-actions>
              <p v-if="isSaveDisabledWithInsurances()" class="disabled_message text-align-right margin-right-15">All insurance details must be entered before you can finalize income.</p>
              <p v-if="isSaveDisabledWithExpenses(true)" class="disabled_message text-align-right margin-right-15">Referral Fee must be entered when a Referrer is selected.</p>
            </group-card>
        </div>


    <floating-comment-section v-model="application" :available-sections="availableSections"
      :current-sections="currentSections" />
    </div>
    <div class="md-layout" v-else>
        <md-empty-state
            md-icon="highlight_off"
            md-label="Application Not Selected"
            md-description="Please select an application before proceeding.">
        </md-empty-state>
    </div>
</template>
<script>
import {mapGetters} from "vuex";
import {GroupCard} from "@/components";
import {PropertyView} from "@/components";
import {NoteView} from "@/components";
import { newSubmission } from "@/schema.js";
import ValidatedInputs from "@/components/ValidatedInput";
import InsuranceComponent from "@/components/InsuranceComponent";
import filters from "@/components/utils/filters";
import LoadingIconTextButton from "../components/LoadingIconTextButton";
import ValidatedAutocompleteInput from "../components/ValidatedInput/ValidatedAutocompleteInput";
import WaitingDialog from "@/components/Dialogs/WaitingDialog";
import ConfirmDialog from "@/components/Dialogs/ConfirmDialog";
import Templates from "./Templates";
import moment from "moment";
import NotificationCard from "../components/Cards/NotificationCard";
import FloatingCommentSection from '@/components/Editors/FloatingCommentSection';
import ListItemCard from "@/components/Cards/ListItemCard.vue";
import utils from "@/components/utils/utils";
import { ValidationObserver } from 'vee-validate';

export default {
  components: {
    NotificationCard,
    Templates,
    InsuranceComponent,
    GroupCard,
    PropertyView,
    NoteView,
    LoadingIconTextButton,
    ValidatedAutocompleteInput,
    ...ValidatedInputs,
    FloatingCommentSection,
    ListItemCard,
  },
  data() {
    return {
      error_dialog: {
        show: false,
        title: '',
        content: '',
      },
      brokerPod: [],
      brokers: [],
      expenses: [],
      isLenderAppIdValid: false,
      single: '',
      file: null,
      new_income: {
        document_origination_fee: {
          amount: 0,
          include_in_naf: false
        },
        lender_commission: {
          amount: 0,
          include_in_naf: false
        },
        insurance_income: {
          amount: 0,
          include_in_naf: false
        },
        totalIncome: 0,
      },
      settlement_date: '',
      cut_off_day: null,
      lender_application_id: '',
      isLenderApplicationIdValid: false,
      compliance: false,
      preventPushToXero: false,
      isSubmitted: false,
      isSettlementDateToFar: false,
      isFinalized: false,
      isSaving: false,
      totalExpenses: '',
      currentLender:[],
      available_products: null,
      document_origination_fee: 0,
      establishment_fee: 0,
      ppsr_fee: 0,
      risk_fee: 0,
      loading_tax_invoice_request_document: false,
      available_insurance_income: {
        cci: 0,
        mvi: 0,
        gap: 0,
        wty: 0,
      },
      submission: newSubmission(),
      expense_warning: {
        message: "Expenses not yet saved.",
        show: false,
      },
      isSettled: false,
      availableSections: [
        'Deal Incomes',
        'Wallet Notification'
      ],
      currentSections: 'Deal Incomes',
    }
  },
  filters: {
    ...filters,
  },
  watch: {
    "application.loan.id"(new_val, old_val){
      if (new_val != old_val) {
        this.getExistingSettlement();
      }
    },
    "application.loan.insurances": {
      handler: function() {
        this.calculateIncome();
      },
      deep: true
    },
    "application_referrer": {
      handler: function() {
        this.calculateReferralFee();
      },
      deep: true
    },
    "submission.document_origination_fee"(new_val){
      this.new_income.document_origination_fee.amount = new_val;
    },
    "new_income.lender_commission": {
      handler: function(newVal) {
        if (Object.hasOwn(this.application.loan.broker_income, 'lender_commission')) {
          this.application.loan.broker_income.lender_commission.include_in_naf = newVal.include_in_naf;
          this.application.loan.broker_income.lender_commission.amount = newVal.amount;
        } else {
          this.application.loan.broker_income.lender_commission = {...newVal};
        }
        this.calculateIncome();
      },
      deep: true
    },
    "expenses": {
      handler: function() {
        if (this.expenses.some(expense => expense.isSaved == false)) {
          this.expense_warning.show = true;
        } else {
          this.expense_warning.show = false;
        }
      },
      deep: true
    },
  },
  computed: {
    ...mapGetters([
      'options',
      'productLenders',
      'products',
      'application',
      'application_referrer',
      'userHasPermission',
      'security',
      'userHasRole',
      'requires_current_privacy_consent',
      'requires_previous_privacy_consent'
    ]),
    list_of_brokers() {
      const adminBrokers = this.$store.state.administration_brokers !== null && this.$store.state.administration_brokers !== undefined
      ? this.$store.state.administration_brokers
      : this.brokers;

      if (adminBrokers) {
        const brokersDetails = adminBrokers.map(broker => ({
          name: `${broker.id} - ${broker.name.first_name} ${broker.name.last_name}`,
        }));
        return brokersDetails;
      }
      return [];
    },
    loan_type() {
      if (this.application) {
        if (this.application.loan.asset.type == 'Personal Unsecured' || this.application.loan.asset.type == 'Personal Secured') {
          return {
            product_type: 'loan',
            application_type: 'consumer',
            asset_type: 'personal',
          };
        } else {
          switch (this.application.loan.type) {
            case 'Sole Trader':
            case 'Partnership - ABN':
            case 'Company':
            case 'Trust':
              return {
                product_type: 'loan',
                application_type: 'commercial',
                asset_type: 'vehicle',
              };
            case 'Consumer':
              return {
                product_type: 'loan',
                application_type: 'consumer',
                asset_type: 'vehicle',
              };
          }
        }
      } else {
        return null;
      }
    },
    settlement_expense_types_values() {
      return Object.values(this.options.settlement_expense_types);
    },
    trade_in_asset() {
      let trade_in_asset = {
        trade_in_value: 0,
        outstanding_amount: 0,
      }
      let asset = this.application.applicants.flatMap( a => a.assets ).find( a => a.has_trade_in );
      if (!!asset) {
        trade_in_asset["trade_in_value"] = asset.value === null ? this.security.trade_in_value : asset.value;

        if (asset.has_liability) {
          trade_in_asset["outstanding_amount"] = asset.finance.outstanding_amount;
        }
      }
      return trade_in_asset;
    },
    total_requested_amount() {
      let requested_amount = this.float(this.security.requested_amount);
      let trade_in_asset = this.trade_in_asset;
      let trade_in_asset_outstanding_amount = this.float(trade_in_asset["outstanding_amount"]);
      let trade_in_value = this.float(trade_in_asset["trade_in_value"]);
      return requested_amount + trade_in_asset_outstanding_amount - trade_in_value;
    },
    total_fees() {
      let document_origination_fee = this.float(this.submission.document_origination_fee);
      let establishment_fee = this.float(this.submission.establishment_fee)
      let ppsr_fee = this.float(this.submission.ppsr_fee);
      let risk_fee = this.float(this.submission.risk_fee);
      return Math.round((document_origination_fee + establishment_fee + ppsr_fee + risk_fee) * 100) / 100;
    },
    total_insurance() {
      return this.application.loan.insurances.reduce(
        (sum, insurance) => sum + ((insurance.include_in_naf && insurance.enabled) ? this.float(insurance.premium) : 0),
        0
      );
    },
    naf: {
      get() {
        if (!this.application) {
          return null;
        }
        let lender_commission_fee = this.new_income.lender_commission.include_in_naf ?
          this.float(this.new_income.lender_commission.amount) :
          0;
        return Math.round((this.total_requested_amount + this.total_fees + this.total_insurance + lender_commission_fee) * 100) / 100;
      },
      set(value) {
        this.submission.naf = value;
      }
    },
  },
  mounted() {
    this.loadCutOffDay();
    this.getExistingSettlement();
    this.fetchBrokers();
  },
  methods: {
    // make all filters available as methods
    ...filters,
    ...utils,
    is_save_disabled(finalize = false) {
      return this.isSaveDisabledWithInsurances() || this.isSaveDisabledWithExpenses(finalize);
    },
    show_payee_details(type) {
      return type === 'Gift Cards' ||
      type === 'Inspection Fees';
    },
    async loadCutOffDay() {
      try {
        let success  = await this.$store.dispatch('get_cut_off_day');
        if (success) {
          this.cut_off_day = this.$store.state.cut_off_day;
        } else {
          console.error('Request not succesfull loading cut off day');
        }
      } catch (error) {
        console.error('Error loading cut off day:', error);
      }
    },
    show_shared_broker(type) {
      return type === 'Shared Broker Fee';
    },
    async fetchBrokers() {
      try {
        let status = await this.$store.dispatch('fetch_administration_brokers', true);
        if (status) {
          this.brokers = this.$store.state.administration_brokers;
        }
      } catch (error) {
        console.error('Error fetching brokers:', error);
      }
    },
    insuranceIncome(insurance) {
      if (insurance.enabled) {
        this.available_insurance_income[insurance.type] = insurance.income;
        return this.available_insurance_income[insurance.type];
      }
      return 0;
    },
    calculateInsuranceIncome(insurance, income) {
      const self = this;
      this.application.loan.insurances.forEach(function (item) {
        if (item.type == insurance.type) {
          self.available_insurance_income[insurance.type] = income;
          if (insurance.enabled) {
            item.income = income;
          }
        }
      });
      this.calculateIncome();
    },
    onFileChange(event) {
      this.file = event.target.files[0];
      this.single = this.file.name;
    },
    setExistingValues(result) {
      this.currentLender.lender_name = result.product_lender.lender_name;
      this.currentLender.product_name = result.product_lender.product_name;
      let {product_type, application_type, asset_type} = this.loan_type;
      this.available_products = this.products(product_type, application_type, asset_type, this.currentLender.lender_name);
      if (result.expenses != null && result.expenses.length != 0) {
        // fillback data which from database
        result.expenses.forEach((expense, index) => {
          this.$set(this.expenses, index, {
            type: expense.type,
            value: expense.value,
            payee_first_name: expense.payee_first_name,
            payee_last_name: expense.payee_last_name,
            payee_email: expense.payee_email,
            broker_shared_fee: this.getFormattedBroker(expense.broker_id_shared_fee),
            isSaved: true
          })
        }, this);
      }
      // auto add expense when it is empty
      if (this.expenses.length === 0) {
        this.addExpense();
      }

      if (result.income != null && result.income.length != 0) {
        this.new_income.insurance_income.amount = result.income.insurance_income.amount ?? 0;
        this.new_income.document_origination_fee.amount = result.income.document_origination_fee.amount ?? 0;
        this.new_income.lender_commission.amount = result.income.lender_commission.amount ?? 0;
        this.new_income.lender_commission.include_in_naf = result.income.lender_commission.include_in_naf ?? false;
      }
      this.settlement_date = result.settlement_date;
      this.lender_application_id = result.lender_application_id;
      // SDB-565 temporary comment the code until further notice, and set compliance flag to ture
      // this.compliance = result.compliance;
      this.compliance = true;
      this.isSubmitted = (result.is_submitted == 1) ? true : false;
      this.isFinalized = (result.has_finalized == 1) ? true : false;

      this.document_origination_fee = result.submission.dof;
      this.establishment_fee = result.submission.est_fee;
      this.ppsr_fee = result.submission.ppsr_fee;
      this.risk_fee = result.submission.risk_fee;

      if (this.isSubmitted == true) {
        this.document_origination_fee = result.document_origination_fee;
        this.establishment_fee = result.establishment_fee;
        this.ppsr_fee = result.ppsr_fee;
        this.risk_fee = result.risk_fee;
        this.showNotification('Income for this application has already been finalized.', 'Info!')
      }
      this.calculateIncome();
      // calculate referrer fee
      // add referrer fee to expenses list when there is a referrer
      if (this.application_referrer.show) {
        let referral_index = result.expenses.findIndex(expense => expense.type === 'Referral Fees');
        // when get referral fee from db, do not add it.
        if (referral_index < 0) {
          this.calculateReferralFee();
        }
      }
      this.calculateExpenses();
    },
    getFormattedBroker(brokerId){
      const broker = this.brokers.find(b => b.id == brokerId);
      if (broker) {
        return `${broker.id} - ${broker.name.first_name} ${broker.name.last_name}`;
      } else {
        return '';
      }
    },
    calculateIncome() {
      let sumOfInsurances = 0;
      this.application.loan.insurances.forEach(function (insurance){
        if (insurance.enabled) {
          insurance.income = insurance.income ?? 0;
          sumOfInsurances += parseFloat(insurance.income);
        }
      });
      this.new_income.totalIncome = (parseFloat(sumOfInsurances) + parseFloat(this.new_income.document_origination_fee.amount ?? 0) + parseFloat(this.new_income.lender_commission.amount ?? 0)).toFixed(2);
      if (isNaN(this.new_income.totalIncome)) {
        this.new_income.totalIncome = 0;
      }
    },
    calculateExpenses() {
      let sumOfExpenses = 0;
      this.expenses.forEach(function (expense){
        expense.value = expense.value ?? 0;
        sumOfExpenses += parseFloat(expense.value);
      });
      this.totalExpenses = parseFloat(sumOfExpenses.toFixed(2));
      if (isNaN(this.totalExpenses)) {
        this.totalExpenses = 0;
      }
    },
    setBrokerPod(index){
      const selectedBroker = this.expenses[index].broker_shared_fee;
      if (selectedBroker) {
        const regex = /(\d+) - .+/; // Captures one or more digits followed by " - " followed by any characters
        const match = selectedBroker.match(regex);
        if (match) {
          const brokerId = match[1];
          const broker = this.brokers.find(b => b.id == brokerId);
          this.brokerPod[index] = broker.broker_pod;
        } else {
          console.log("No match found from selected broker");
        }
      }
    },
    calculateReferralFee() {
      if (this.application_referrer.show) {
        let referral_index = this.expenses.findIndex(expense => expense.type === 'Referral Fees');
        let value = 0;
        switch (this.application_referrer.commission.type) {
          case 'percentage':
            value = (this.new_income.totalIncome * this.application_referrer.commission.rate / 100).toFixed(2);
            break;
          case 'amount':
            value = this.application_referrer.commission.rate;
            break;
        }
        // not found in db and application not yet finalized
        if (referral_index < 0 && !this.isFinalized) {
          this.$set(this.expenses, 0, {
            type: "Referral Fees",
            value: value,
            isSaved: false,
          });
        }
      }
    },
    isSaveDisabledWithExpenses(finalize = false) {
      if (this.application_referrer.show && finalize) {
        return !this.expenses.some(expense => expense.type === 'Referral Fees');
      } else {
        return false;
      }
    },
    addExpense() {
      this.$set(this.expenses, this.expenses.length, {
        type: 'Gift Cards',
        value: 0,
        payee_first_name: '',
        payee_last_name: '',
        payee_email: '',
        broker_shared_fee: null,
        isSaved: false,
      });
    },
    removeItem(item) {
      let index = this.expenses.indexOf(item);
      if (index >= 0){
        this.expenses.splice(index, 1);
      }
    },
    isSaveDisabledWithInsurances() {
      // disable save when insurance is selected but not filled except policy number
      return this.application.loan.insurances.some(insurance => {
        if (insurance.enabled) {
          if (!!insurance.insurer
            && !!insurance.policy
            && (insurance.income === 0 || !!insurance.income)
            && insurance.term !== null
            && insurance.premium !== null
            && insurance.policy_number
            && !!insurance.insurer.trim()
            && !!insurance.policy.trim()) {
              return false;
          } else {
            return true;
          }
        } else {
          return false;
        }
      });
    },
    async generateInvoice() {
      this.loading_tax_invoice_request_document = true;
      this.showModal('Once its generated you will be redirected to documents page.', 'In Process');
      await this.save('saveIncome', false);
      try {
        await this.$store.dispatch('generate_tax_invoice_request', this.application.loan.id);
        this.$router.push(`/application/${this.application.loan.id}/documents`);
      } catch (err) {
        this.showNotification('Failed Generating Tax Invoice Request Document', 'Error!')
      } finally {
        if (this.$modal.submit) {
          this.$modal.submit();
        }
      }
      this.loading_tax_invoice_request_document = false;
    },
    async generateBrokCommInvoice() {
      this.loading_tax_invoice_request_document = true;
      this.showModal('Once its generated you will be redirected to documents page.', 'In Process');
      await this.save('saveIncome', false);
      let response = await this.$store.dispatch('generate_brokerage_commission_invoice', this.application.loan.id);
      if (this.$modal.submit) {
        this.$modal.submit();
      }
      if (response) {
        this.$router.push(`/application/${this.application.loan.id}/documents`);
      } else {
        this.showNotification('Failed Generating Tax Invoice Request Document', 'Error!')
      }
      this.loading_tax_invoice_request_document = false;
    },
    async save(submitted, showDialog = true) {
      let processingMessageBegin = 'Please wait while we save the income.'
      let processingMessageComplete = 'Application has been saved as a deal and incomes has been locked.'
      switch (submitted) {
        case 'saveIncomeAndFinalize':
          submitted = 1;
          break;
        case 'saveUpdate':
          submitted = 1;
          this.preventPushToXero = true;
          processingMessageBegin = 'Please wait while we update data.'
          processingMessageComplete = 'Application data has been updated.'
          break;
        default:
          submitted = 0;
          break;
      }
      if (submitted) {
        // check all applicants current and previous privacy consent
        // Disable temporary SDB-1657
        // if (this.requires_current_privacy_consent && this.requires_previous_privacy_consent) {
        //   if (this.$modal.submit) {
        //     this.$modal.submit();
        //   }
        //   this.showNotification('Application cannot be finalised without any valid privacy consent from all applicants.', 'Error!');
        //   return;
        // }

        //get todays date in dd/mm/yyyy
        let today = moment(new Date()).format("YYYY-MM-DD");
        if (this.settlement_date == null || this.settlement_date.length == 0) {
          this.showNotification('Settlement Date is required to finalize the income.', 'Error!');
          return;
        } else{

          //if settlement date is in future
          if (this.settlement_date > today) {
            this.showNotification('Settlement Date cannot be in future.', 'Error!');
            return;
          }
        }

        let first_of_current = moment().format("YYYY-MM-01");
        let last_day_of_current_month = moment().endOf('month').date();
        // This ensures that the cut-off day does not exceed the actual number of days in the current month
        let valid_cut_off_day = Math.min(this.cut_off_day, last_day_of_current_month);
        let cut_off_of_current = moment().date(valid_cut_off_day).format("YYYY-MM-DD");
        let first_of_last = moment().startOf('month').subtract(1, 'month').format("YYYY-MM-DD")
        // limit normal users to finalize in previous month, if its already passed cut off day for current month;
        // max allowable backdating is 1st of the previous month
        // admin users can finalize in any month;
        
        //if settlement date is in future
        if (((this.settlement_date < first_of_current && today > cut_off_of_current) ||
            (today < cut_off_of_current && this.settlement_date < first_of_last)) &&
            !this.userHasPermission('update finalized-deal-income')) {
            this.isSettlementDateToFar = true;
            this.showNotification('Cannot finalize this deal, please contact support', 'Error!');
            return;
        }

        if (this.new_income.totalIncome < 1) {
          let response = await this.$modal.show(ConfirmDialog, {
            title: 'Deal Income < $1',
            message: 'Please confirm finalizing $0 income.',
            confirmText: 'Yes',
            cancelText: 'Cancel',
          });
          if (!response) {
            return;
          }
        }
        //if broker isnt set show error
        if (!this.hasBroker()) {
          this.showNotification('Broker needs to be assigned to finalize the income.', 'Please assign broker.');
          return;
        }
        //if there is referrer amount but no referrer set show error
        if (!this.hasReferrer() && this.getExpenseAmount('Referral Fees') > 0) {
          this.showNotification('Referral needs to be assigned to finalize the income when there is a Referral Expenses.', 'Please assign Referral.');
          return;
        }

        const payeeDetailsValid = await this.checkPayeeDetails();
        if (!payeeDetailsValid) {
          this.showNotification('Payee Details must be provided for Gift Card/Inspection Fee deal expense', 'Error!');
          return;
        }

        const brokerSharedFeeExists = await this.checkSharedBroker();
        if (!brokerSharedFeeExists) {
          this.showNotification('A Broker must be provided for Shared Broker Fee deal expense', 'Error!');
          return;
        }

        await this.checkLenderAppId().then(isIdValid => {
          this.isLenderApplicationIdValid = isIdValid;
          if(!isIdValid) {
            this.showNotification('A valid Lender Application Id is required to finalize the income.', 'Error!');
          }
        });

        if(!this.isLenderApplicationIdValid) {
          return;
        }

      }
      let forCompliance = {
        'application': this.application,
        'other': {
          'new_income': this.new_income,
          'expenses': this.expenses,
          'lender_application_id': this.lender_application_id,
          'settlement_date': this.settlement_date,
          'preventPushToXero': this.preventPushToXero,
        },
        'is_submitted': submitted
      }
      const config = {
        headers: {'content-type': 'multipart/form-data'}
      }

      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('for_compliance', JSON.stringify(forCompliance))
      if(showDialog) {
        this.showModal('Processing', processingMessageBegin);
      }
      await this.$store.dispatch('save_application', this.application);
      let result = await this.$store.dispatch('submit_for_compliance', formData, config);
      if (showDialog) {
        if (result == true) {
          // if the waiting dialog is open, close it
          if (this.$modal.submit) {
            this.$modal.submit();
          }
          if (submitted) {
            this.showNotification(processingMessageComplete, 'Success');
            this.isSubmitted = true;
          } else {
            let message = "Deal Income has been saved. ";
            if (!this.isSettled) {
              message += "Expenses not yet saved.";
            }
            this.showNotification(message, 'Success');
          }
          // set expense saved when application is settled
          if (this.isSettled) {
            this.expenses.forEach((_, index) => {
              this.$set(this.expenses[index], 'isSaved', true);
            }, this);
          }
        } else {
          // if the waiting dialog is open, close it
          if (this.$modal.submit) {
            this.$modal.submit();
          }
          this.showNotification('Cannot finalize this deal, please contact support', 'Error');
        }
      }
    },
    hasBroker() {
      let owners = this.application.owners;
      //get element whose role is broker
      let broker = owners.find(owner => owner.role == 'Broker');
      //check if broker has user_id or not
      if (broker && broker.user_id) {
        return true;
      }
      return false;
    },
    hasReferrer() {
      let owners = this.application.owners;
      //get element whose role is broker
      let referrer = owners.find(owner => owner.role == 'Referrer');
      //check if broker has user_id or not
      if (referrer && referrer.user_id) {
        return true;
      }
      return false;
    },
    getExpenseAmount(type) {
      if (type) {
        let expAmount = 0;
        //from expense find matching type and sum its amount
        this.expenses.forEach(expense => {
          if (expense.type == type) {
            expAmount += filters.float(expense.value);
          }
        });
        return expAmount;
      }
    },
    checkLenderAppId() {
      const observerInstance = this.$refs.lender_application_id_observer;
      return observerInstance.validate().then(isValid => {
        return isValid;
      });
    },
    changeExpenseStatus(key, calculateExpense=false) {
      this.$set(this.expenses[key], "isSaved", false);
      if (calculateExpense) {
        this.calculateExpenses();
      }
    },
    checkPayeeDetails() {
      // Filter expenses by types that required payee details
      const filteredExpenses = this.expenses.filter(expense => {
        return (
          expense.type === 'Gift Cards' ||
          expense.type === 'Inspection Fees'
        );
      });

      // If no filtered expenses
      // return true as they are not needed for provided deal incomes if any
      if (filteredExpenses.length === 0) {
        return true;
      }

      // Check if any filtered expense has valid payee details
      const hasValidPayeeDetails = filteredExpenses.some(expense => {
        const { payee_first_name, payee_last_name, payee_email } = expense;
        return (
          payee_first_name?.trim() !== '' &&
          payee_last_name?.trim() !== '' &&
          payee_email?.trim() !== ''
        );
      });

      return hasValidPayeeDetails;
    },
    checkSharedBroker(){
      // Filter expenses by types that required payee details
      const filteredExpenses = this.expenses.filter(expense => {
        return (expense.type === 'Shared Broker Fee');
      });

      // If no Shared Broker Fee make it not required
      if (filteredExpenses.length === 0) {
        return true;
      }

      const hasRequiredSharedBroker = filteredExpenses.some(expense => {
        const { broker_shared_fee } = expense;
        const isBrokerSelected = this.list_of_brokers.some(broker => {
          return broker.broker_shared_fee === broker_shared_fee.name;
        });
        return isBrokerSelected;
      });

      return hasRequiredSharedBroker;
    },
    async getExistingSettlement() {
      let result = await this.$store.dispatch('get_existing_settlement', this.application.loan.id);
      if (result != false) {
        this.setSubmission(result);
        if (result.id == null) {
          let msg = "This application has not been requested for Settlement, ";
          msg += "but you can still save the incomes. ";
          msg += "Expenses will not save before application is settled.";
          this.showNotification(msg, 'Warning!');
          //save incomes
          this.new_income.insurance_income.amount = result.income.insurance_income?.amount ?? 0;
          this.new_income.document_origination_fee.amount = result.income.document_origination_fee?.amount ?? 0;
          this.new_income.lender_commission.amount = result.income.lender_commission?.amount ?? 0;
          this.new_income.lender_commission.include_in_naf = result.income.lender_commission?.include_in_naf ?? false;
          this.calculateIncome();
          this.addExpense();
        } else {
          this.isSettled = true;
          this.setExistingValues(result)
        }
      } else {
        this.showNotification('Error while getting existing settlement.', 'Error');
        this.isSubmitted = true;
      }
    },
    setSubmission(settleData) {
      if (settleData.id == null) {
        // no settlement
        if (this.application.loan.submission.previous.length > 0) {
          this.submission = this.application.loan.submission.previous[0];
        } else {
          this.submission = this.application.loan.submission.current;
        }
      } else {
        // get submission from previous
        let index = this.application.loan.submission.previous.findIndex(prevSubmission => prevSubmission.id == settleData.submission_id);
        if (index === -1) {
          this.submission = this.application.loan.submission.previous[0];
        } else {
          this.submission = this.application.loan.submission.previous[index];
        }
      }
    },
    showNotification(message, title) {
      this.error_dialog.show = true;
      this.error_dialog.content = message;
      this.error_dialog.title = title;
    },
    showModal(title, message){
      this.$modal.show(WaitingDialog, {
        title: title,
        message: message,
      });
    },
  },
  async beforeCreate() {
    const params = {
      types: [
        'settlement_expense_types',
      ],
    }
    await this.$store.dispatch('fetch_types', { params });
  },
};
</script>
<style scoped lang="scss">
@import "@/assets/scss/theme.scss";

.list-item {
  margin-bottom: 25px;
  padding-top: 15px;
  padding-bottom: 15px;
}

.expense-warning {
  padding-top: 30px !important;
}

.floating-bottom-right{
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  & button.fab{
    width: 50px;
    height: 50px;
    border-radius: 50%;
  }
}
.md-card{
  height: 100%;
}
.md-layout-item:not(.md-button, .md-checkbox) {
  padding-top: 15px;
}

.generate_dropdown{
  margin-right: 5px;
}

.disabled_message {
  color: red;
  font-size: 15px;
}
.success_message {
  color: green;
  font-size: 15px;
}
.margin-right-15{
  margin-right: 15px;
}
.text-align-right {
  text-align: right;
}
.mr-lt-15 {
  margin-left: 15px;
}

.lender_app_id_input {
  margin-top: -10px;
}

.close_button {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.my-2 {
  margin: 2rem 0;
}
</style>
